import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { Address, AddressProducts } from '@mwe/models';
import { NgbDropdown, NgbDropdownButtonItem, NgbDropdownItem, NgbDropdownMenu, NgbDropdownToggle } from '@ng-bootstrap/ng-bootstrap';
import { NgClass } from '@angular/common';
import { GetAddressPostCodeAndStreetPipe } from '@mwe/ui/pipes';
import { TranslatePipe } from '@ngx-translate/core';
import { tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import ShortUniqueId from 'short-unique-id';

@Component({
  selector: 'mwe-address-with-selection-dropdown',
  templateUrl: './address-with-selection-dropdown.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgbDropdown,
    NgbDropdownToggle,
    NgClass,
    NgbDropdownMenu,
    NgbDropdownButtonItem,
    NgbDropdownItem,
    GetAddressPostCodeAndStreetPipe,
    TranslatePipe,
  ],
})
export class AddressWithSelectionDropdownComponent {
  destroyRef = inject(DestroyRef);
  address = input.required<Address>();
  addressOptions = input.required<AddressProducts[]>();
  readonly newAddressSelected = output<AddressProducts>();
  dropdown = viewChild(NgbDropdown);
  readonly buttonEle = viewChild<ElementRef<HTMLButtonElement>>('buttonElement');
  isCollapsed = signal<boolean>(true);
  private readonly _componentId = signal<string>(new ShortUniqueId({ dictionary: 'alpha' }).rnd(8));
  readonly dropdownId = computed<string>(() => `${this._componentId()}_dropdown`);
  readonly buttonId = computed<string>(() => `${this._componentId()}_button`);
  readonly labelId = computed<string>(() => `${this._componentId()}_label`);
  readonly selectedOptionIndex = computed<number>(() => {
    return this.addressOptions().indexOf(this.addressOptions().find(value => value.address.hashCode === this.address().hashCode));
  });

  constructor() {
    effect(() => {
      this.dropdown()
        ?.openChange?.pipe(
          takeUntilDestroyed(this.destroyRef),
          tap(isOpen => this.isCollapsed.set(!isOpen)),
        )
        .subscribe();
    });
  }

  closeDropDown(): void {
    this.dropdown()?.close();
  }

  isAddressSelected(address: Address): boolean {
    return address.hashCode === this.address().hashCode;
  }

  onAddressSelected(addressProducts: AddressProducts): void {
    this.newAddressSelected.emit(addressProducts);
    this.buttonEle().nativeElement.focus();
  }
}
